import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Validates an email address format
 * Excludes email addresses with aliases (e.g. "john+alias@gmail.com")
 */
export function validateEmail(email: string): boolean {
  return /^[^\s@+]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Removes the email alias (the part after + and before @) from an email address
 * Example: "john+alias@gmail.com" -> "john@gmail.com"
 */
export function getEmailWithoutAlias(email: string): string {
  if (!email || !email.includes('@') || !email.includes('+') || !validateEmail(email)) return email;
  
  const [localPart, domain] = email.split('@');
  const cleanLocalPart = localPart.split('+')[0];
  
  return `${cleanLocalPart}@${domain}`;
}

/**
 * Get full storage URL from Supabase storage path
 * @param path Supabase storage path (starting with bucket name)
 * @returns Full storage URL
 */
export function getMediaUrl(path?: string): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const base = import.meta.env.VITE_MEDIA_STORAGE_BASE_URL || '';
  // handle public and authenticated paths
  // https://supabase.com/docs/guides/storage/serving/downloads
  const relativePath = path.replace(/^\//, ''); // ensure no leading slash
  const pathWithAuth = relativePath.startsWith('publicm/') 
    ? `/public/${relativePath}`
    : `/authenticated/${relativePath}`;
  // Ensure no double slashes
  return base.replace(/\/$/, '') + '/' + pathWithAuth.replace(/^\//, '');
}

/** Reverse of getMediaUrl: get Supabase storage path from full storage URL
 * @param url Full storage URL
 * @returns Supabase storage path (starting with bucket name, relative path without leading slash)
 */
export function getStoragePathFromUrl(url: string): string {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url)) return url;
  const base = import.meta.env.VITE_MEDIA_STORAGE_BASE_URL || '';
  return url.replace(base, '')
    .replace(/(public|authenticated)\//, '') // remove auth path prefix
    .replace(/^\//, '');
}

/**
 * Returns true if the url is a video (mp4 or webm)
 */
export function isVideoUrl(url?: string): boolean {
  if (!url) return false;
  return /\.(mp4|webm)($|\?)/i.test(url);
}

export function getDisplayName(character: { first_name?: string | null; last_name?: string | null }) {
  const first = character.first_name?.trim() || '';
  const last = character.last_name?.trim() || '';
  return (first + ' ' + last).trim();
}

export function isCustomCharacter(character: { parent_char_id?: string | null; character_scope: string }) {
  return !character.parent_char_id && character.character_scope === 'private';
}

export function slugifyName(first: string, last?: string): string {
  const base = getDisplayName({ first_name: first, last_name: last });
  return base
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanum with hyphen
    .replace(/^-+|-+$/g, '') // Trim hyphens
    .replace(/--+/g, '-'); // Collapse multiple hyphens
}

/**
 * Omits specific keys from an object
 * @param obj - The object to omit keys from
 * @param keys - The keys to omit
 * @returns A new object with the specified keys omitted
 */
export function omit< T extends object, K extends keyof T >(
  obj: T,
  keys: K[]
): Omit< T, K > {
  return keys.reduce((r, key ) => (delete r[ key ], r), {
    ...obj,
  });
}

/**
 * Formats a timestamp into a human-readable date and time string with current locale
 * Includes date if it's not the same day as today in the current locale
 * @param timestamp - The timestamp to format
 * @returns The formatted date and time string
 */
export function formatMessageTime(timestamp?: string) {
  if (!timestamp) return '';
  try {
    const today = new Date();
    const date = new Date(timestamp);
    const isSameDay = date.toLocaleDateString() === today.toLocaleDateString();
    const format: Intl.DateTimeFormatOptions = isSameDay ? {
      hour: 'numeric',
      minute: 'numeric',
    } : {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    };
    return new Intl.DateTimeFormat('default', format).format(date);
  } catch (e) {
    if (import.meta.env.DEV) console.error('[DEV] Error formatting message time:', e);
    return '';
  }
}

/**
 * Formats a date to a readable string
 */
export function formatDate(date: Date | string, formatOptions: Intl.DateTimeFormatOptions = {}): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString(undefined, { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    ...formatOptions,
  });
}

export function formatDecimal(value: number, precision: number = 1): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: precision })
}

/**
 * Shuffles an array in place -  Durstenfeld shuffle
 * @param array - The array to shuffle
 * @returns The shuffled array for convenience
 */
export function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Creates a debounced version of a function that delays its execution
 * until after wait milliseconds have elapsed since the last time it was invoked
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the function
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function debounced(this: any, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), wait);
  };
}
